import type Konva from "konva";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Circle, Group, Image as KonvaImage, Rect, Text, Transformer } from "react-konva";
import { solidOf } from "../../lib/position-tone.js";
import soccerBallUrl from "./assets/soccer-ball.svg?url";

/** Tiny replacement for the `use-image` package — loads an HTMLImageElement. */
function useImage(url: string | null): HTMLImageElement | null {
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  useEffect(() => {
    if (!url) {
      setImg(null);
      return;
    }
    const el = new window.Image();
    el.crossOrigin = "anonymous";
    let active = true;
    el.onload = () => {
      if (active) setImg(el);
    };
    el.onerror = () => {
      if (active) setImg(null);
    };
    el.src = url;
    return () => {
      active = false;
    };
  }, [url]);
  return img;
}

import type { PlayerRow } from "../../repos/players.js";
import type { FieldKind, NameDisplay } from "./scene.js";
import {
  clampTokenScale,
  TOKEN_SIZE_PRESET_FACTOR,
  type Token,
  type TokenSizePreset,
  tokenDisplayName,
} from "./scene.js";

/** Light fields (white paper / zones grid) need dark text; green pitches need white. */
function nameColorFor(fieldKind: FieldKind): string {
  return fieldKind === "pitch_blank" || fieldKind === "pitch_zones" ? "#0b1220" : "#ffffff";
}

/**
 * Tokens layer — players, opponents, balls drawn on top of the pitch.
 *
 * Each token is a draggable Konva Group. Konva drags shapes per-pointer,
 * so multiple fingers can drag different tokens simultaneously once the
 * stage-level multi-touch flags are enabled (see `editor.tsx`).
 *
 * Selected tokens (in `select` mode) get a Konva Transformer attached so the
 * coach can resize a single token or a multi-selection of tokens in one
 * gesture. The Transformer keeps the aspect ratio, scales around the
 * centre, and on `transformend` we read the per-node scale + position back
 * into the scene state (`onResizeEnd`).
 */

interface TokensLayerProps {
  tokens: Token[];
  players: Map<string, PlayerRow>;
  fieldKind: FieldKind;
  sizePreset: TokenSizePreset;
  /** How player names are labelled below each token. */
  nameDisplay: NameDisplay;
  selectedIds: Set<string>;
  /** When true, mounts a Transformer that lets the user resize selected tokens. */
  transformEnabled: boolean;
  onSelect: (id: string | null) => void;
  onDragStart?: (id: string, x: number, y: number) => void;
  onDragMove?: (id: string, x: number, y: number) => void;
  onDragEnd: (id: string, x: number, y: number) => void;
  onLongPress: (id: string, screenX: number, screenY: number) => void;
  /**
   * Called when the user finishes a Transformer gesture. The map contains
   * one entry per affected token with its NEW absolute `scale`, `x`, `y`.
   * The caller is expected to commit these via a single reducer dispatch
   * so the whole gesture becomes one undo step.
   */
  onResizeEnd?: (patches: Map<string, Partial<Token>>) => void;
}

// Token size in board-space units (board is 1920×1358, see BOARD_HEIGHT).
// Deliberately absolute: token radii do not scale when the board does. The actual
// radius rendered is `BASE * preset_factor * (token.scale ?? 1)`.
const TOKEN_RADIUS_BASE = 38;
const BALL_RADIUS_BASE = 22;
const LONG_PRESS_MS = 550;
const DRAG_CANCEL_PX = 6;

/** Gap between the chip and its name plate, at base radius. */
const NAME_GAP = 10;
/** Name-plate font size at base radius. */
const NAME_FONT = 18;

let measureCtx: CanvasRenderingContext2D | null | undefined;
/** Width of a single line of name-plate text (Konva's default Arial, weight 600). */
function measureNameWidth(text: string, fontSize: number): number {
  if (measureCtx === undefined) measureCtx = document.createElement("canvas").getContext("2d");
  if (!measureCtx) return text.length * fontSize * 0.55;
  measureCtx.font = `600 ${fontSize}px Arial`;
  let w = 0;
  for (const line of text.split("\n")) w = Math.max(w, measureCtx.measureText(line).width);
  return w;
}

/**
 * Invisible grab box spanning the chip AND the name plate below it, so a tap
 * in the gap between the two (or on the name itself) picks up the token
 * instead of falling through to the stage and panning the field. Kept within
 * the name plate's own text box, so the Transformer bounds don't grow.
 */
function tokenHitBox(
  radius: number,
  label: string,
): { x: number; y: number; width: number; height: number } {
  const k = radius / TOKEN_RADIUS_BASE;
  const top = -radius;
  if (!label) return { x: -radius, y: top, width: radius * 2, height: radius * 2 };
  const fontSize = NAME_FONT * k;
  const plateWidth = radius * 4;
  const textWidth = measureNameWidth(label, fontSize);
  const lines = Math.max(1, Math.ceil(textWidth / plateWidth));
  const halfW = Math.max(radius, Math.min(plateWidth, textWidth + 8 * k) / 2);
  // Name plate sits at a fixed (unscaled) gap below the chip — see TokenShape.
  const bottom = radius + NAME_GAP + fontSize * lines;
  return { x: -halfW, y: top, width: halfW * 2, height: bottom - top };
}

function tokenEffectiveRadius(token: Token, preset: TokenSizePreset): number {
  const base = token.kind === "ball" ? BALL_RADIUS_BASE : TOKEN_RADIUS_BASE;
  const presetMul = TOKEN_SIZE_PRESET_FACTOR[preset] ?? 1;
  const tokenMul = token.scale ?? 1;
  return base * presetMul * tokenMul;
}

export function TokensLayer({
  tokens,
  players,
  fieldKind,
  sizePreset,
  nameDisplay,
  selectedIds,
  transformEnabled,
  onSelect,
  onDragStart,
  onDragMove,
  onDragEnd,
  onLongPress,
  onResizeEnd,
}: TokensLayerProps) {
  // Map of token-id → live Konva Group so the Transformer can attach
  // to multi-selections in one gesture. Populated via callback refs.
  const nodeRefs = useRef<Map<string, Konva.Group>>(new Map());
  const registerNode = useCallback((id: string, node: Konva.Group | null) => {
    if (node) nodeRefs.current.set(id, node);
    else nodeRefs.current.delete(id);
  }, []);

  const trRef = useRef<Konva.Transformer | null>(null);

  // Re-attach the Transformer whenever the selection (or its enablement)
  // changes. Also re-run if tokens are added/removed so freshly mounted
  // nodes register in time.
  useEffect(() => {
    const tr = trRef.current;
    if (!tr) return;
    if (!transformEnabled || selectedIds.size === 0) {
      tr.nodes([]);
      tr.getLayer()?.batchDraw();
      return;
    }
    const nodes: Konva.Node[] = [];
    selectedIds.forEach((id) => {
      const n = nodeRefs.current.get(id);
      if (n) nodes.push(n);
    });
    tr.nodes(nodes);
    tr.getLayer()?.batchDraw();
  }, [selectedIds, transformEnabled, tokens]);

  const commitResize = useCallback(() => {
    const tr = trRef.current;
    if (!tr || !onResizeEnd) return;
    const patches = new Map<string, Partial<Token>>();
    for (const node of tr.nodes()) {
      const rawId = node.id();
      if (!rawId.startsWith("tok-")) continue;
      const id = rawId.slice(4);
      const prev = tokens.find((t) => t.id === id);
      if (!prev) continue;
      // keepRatio=true → scaleX === scaleY (we still average defensively).
      const sx = node.scaleX();
      const sy = node.scaleY();
      const mul = (Math.abs(sx) + Math.abs(sy)) / 2;
      if (!Number.isFinite(mul) || mul <= 0) continue;
      const prevScale = prev.scale ?? 1;
      const nextScale = clampTokenScale(prevScale * mul);
      patches.set(id, {
        scale: nextScale,
        x: node.x(),
        y: node.y(),
      });
      // Reset the Konva-internal scale — the next React render will paint
      // the children at the new size derived from `token.scale`.
      node.scaleX(1);
      node.scaleY(1);
    }
    if (patches.size > 0) onResizeEnd(patches);
  }, [onResizeEnd, tokens]);

  return (
    <>
      {tokens.map((token) => (
        <TokenShape
          key={token.id}
          token={token}
          player={token.playerId ? (players.get(token.playerId) ?? null) : null}
          fieldKind={fieldKind}
          nameDisplay={nameDisplay}
          radius={tokenEffectiveRadius(token, sizePreset)}
          selected={selectedIds.has(token.id)}
          onSelect={onSelect}
          onDragStart={onDragStart}
          onDragMove={onDragMove}
          onDragEnd={onDragEnd}
          onLongPress={onLongPress}
          registerNode={registerNode}
        />
      ))}
      <Transformer
        ref={(node) => {
          trRef.current = node;
        }}
        rotateEnabled={false}
        flipEnabled={false}
        keepRatio
        centeredScaling
        ignoreStroke
        enabledAnchors={["top-left", "top-right", "bottom-left", "bottom-right"]}
        anchorSize={12}
        anchorStroke="#97fc00"
        anchorFill="#0b1220"
        anchorCornerRadius={3}
        borderStroke="#97fc00"
        borderStrokeWidth={1.5}
        borderDash={[4, 4]}
        // Soft floor so the user can't shrink a token below ~30 % of base
        // (still legible) — finer clamping is enforced on commit.
        boundBoxFunc={(oldBox, newBox) => {
          if (newBox.width < 12 || newBox.height < 12) return oldBox;
          return newBox;
        }}
        onTransformEnd={commitResize}
      />
    </>
  );
}

interface TokenShapeProps {
  token: Token;
  player: PlayerRow | null;
  fieldKind: FieldKind;
  nameDisplay: NameDisplay;
  radius: number;
  selected: boolean;
  onSelect: (id: string | null) => void;
  onDragStart?: (id: string, x: number, y: number) => void;
  onDragMove?: (id: string, x: number, y: number) => void;
  onDragEnd: (id: string, x: number, y: number) => void;
  onLongPress: (id: string, screenX: number, screenY: number) => void;
  registerNode: (id: string, node: Konva.Group | null) => void;
}

function TokenShape({
  token,
  player,
  fieldKind,
  nameDisplay,
  radius,
  selected,
  onSelect,
  onDragStart,
  onDragMove,
  onDragEnd,
  onLongPress,
  registerNode,
}: TokenShapeProps) {
  // Long-press tracking (Konva exposes underlying pointer events here)
  const longPress = useMemo(
    () => ({
      timer: null as ReturnType<typeof setTimeout> | null,
      startX: 0,
      startY: 0,
      fired: false,
    }),
    // token.id stable enough that we keep one closure per token
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token.id],
  );

  const cancelLongPress = () => {
    if (longPress.timer) {
      clearTimeout(longPress.timer);
      longPress.timer = null;
    }
  };

  if (token.kind === "ball") {
    return (
      <BallToken
        token={token}
        radius={radius}
        selected={selected}
        onSelect={onSelect}
        onDragStart={onDragStart}
        onDragMove={onDragMove}
        onDragEnd={onDragEnd}
        registerNode={registerNode}
      />
    );
  }

  // Player / Opponent share the circular chip layout
  const number =
    token.kind === "player"
      ? (player?.shirt_number ?? token.number ?? null)
      : (token.number ?? null);
  // Name-plate text: abbreviation applies to real roster players only; dummies
  // and opponents keep their own labels (see `tokenDisplayName`).
  const label = tokenDisplayName(token, player?.name ?? null, nameDisplay);
  // Raw name used only for the in-chip initials fallback (no photo, no number).
  const rawLabel =
    player?.name ?? token.label ?? (token.kind === "opponent" ? "Gegner" : "Spieler");
  const tone =
    token.kind === "player"
      ? solidOf(player?.position ?? null)
      : { fill: "#e23a4a", stroke: "#460a10", fg: "#fff1f2" };

  const avatarUrl = token.kind === "player" ? (player?.image_ref ?? null) : null;
  const hitBox = tokenHitBox(radius, label);

  return (
    <Group
      id={`tok-${token.id}`}
      x={token.x}
      y={token.y}
      draggable
      ref={(node) => registerNode(token.id, node)}
      onMouseDown={(e) => {
        e.cancelBubble = true;
        if (!selected) onSelect(token.id);
        longPress.fired = false;
        longPress.startX = e.target.x();
        longPress.startY = e.target.y();
        cancelLongPress();
        const evt = e.evt as MouseEvent;
        longPress.timer = setTimeout(() => {
          longPress.fired = true;
          onLongPress(token.id, evt.clientX, evt.clientY);
        }, LONG_PRESS_MS);
      }}
      onTouchStart={(e) => {
        e.cancelBubble = true;
        if (!selected) onSelect(token.id);
        longPress.fired = false;
        longPress.startX = e.target.x();
        longPress.startY = e.target.y();
        cancelLongPress();
        const t = (e.evt as TouchEvent).touches?.[0];
        if (!t) return;
        const cx = t.clientX;
        const cy = t.clientY;
        longPress.timer = setTimeout(() => {
          longPress.fired = true;
          onLongPress(token.id, cx, cy);
        }, LONG_PRESS_MS);
      }}
      onMouseUp={cancelLongPress}
      onTouchEnd={cancelLongPress}
      onDragStart={(e) => {
        cancelLongPress();
        onDragStart?.(token.id, e.target.x(), e.target.y());
      }}
      onDragMove={(e) => {
        const dx = e.target.x() - longPress.startX;
        const dy = e.target.y() - longPress.startY;
        if (dx * dx + dy * dy > DRAG_CANCEL_PX * DRAG_CANCEL_PX) {
          cancelLongPress();
        }
        onDragMove?.(token.id, e.target.x(), e.target.y());
      }}
      onDragEnd={(e) => {
        cancelLongPress();
        onDragEnd(token.id, e.target.x(), e.target.y());
      }}
    >
      {/* Transparent grab box (chip + name plate) — hit-testing only. */}
      <Rect {...hitBox} fill="rgba(0,0,0,0)" />
      {selected && (
        <Circle
          radius={radius + 6}
          stroke="#97fc00"
          strokeWidth={3}
          dash={[6, 4]}
          listening={false}
        />
      )}
      <Circle
        radius={radius}
        fill={tone.fill}
        stroke={tone.stroke}
        strokeWidth={3}
        shadowColor="black"
        shadowBlur={8}
        shadowOpacity={0.4}
        shadowOffsetY={2}
      />
      {avatarUrl ? (
        <AvatarClip url={avatarUrl} radius={radius - 4} />
      ) : (
        <Text
          text={String(number ?? initials(rawLabel))}
          fontSize={(number != null ? 30 : 24) * (radius / TOKEN_RADIUS_BASE)}
          fontStyle="700"
          fill={tone.fg}
          x={-radius}
          y={-radius}
          width={radius * 2}
          height={radius * 2}
          align="center"
          verticalAlign="middle"
          listening={false}
        />
      )}
      {/* When an avatar hides the number, surface it in a small overlapping
          badge in the bottom-left corner, in the token's own tone colours. */}
      {avatarUrl && number != null && <NumberBadge radius={radius} number={number} tone={tone} />}
      {/* Name plate below the chip. Color picks contrast against the field:
          white on green/half pitches, black on blank/zones backgrounds.
          No stroke — plain text per design. Shown for players, dummies and
          opponents (balls return earlier and never reach this branch). */}
      {label && (
        <Text
          text={label}
          fontSize={NAME_FONT * (radius / TOKEN_RADIUS_BASE)}
          fontStyle="600"
          fill={nameColorFor(fieldKind)}
          x={-radius * 2}
          y={radius + NAME_GAP}
          width={radius * 4}
          align="center"
          listening={false}
        />
      )}
    </Group>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/**
 * Small jersey-number badge that overlaps the bottom-left of a player chip.
 * Used when the chip shows an avatar photo (which otherwise hides the number).
 * Uses the token's own tone so it reads as part of the same chip.
 */
function NumberBadge({
  radius,
  number,
  tone,
}: {
  radius: number;
  number: number;
  tone: { fill: string; stroke: string; fg: string };
}) {
  const br = radius * 0.5;
  // Sit on the lower-left edge of the chip so the two circles overlap.
  const offset = radius * 0.66;
  return (
    <Group x={-offset} y={offset} listening={false}>
      <Circle
        radius={br}
        fill={tone.fill}
        stroke={tone.stroke}
        strokeWidth={2}
        shadowColor="black"
        shadowBlur={4}
        shadowOpacity={0.35}
        shadowOffsetY={1}
      />
      <Text
        text={String(number)}
        fontSize={br * 1.15}
        fontStyle="700"
        fill={tone.fg}
        x={-br}
        y={-br}
        width={br * 2}
        height={br * 2}
        align="center"
        verticalAlign="middle"
        listening={false}
      />
    </Group>
  );
}

/** Circular avatar — Konva has no native clip-to-circle on Image, so we use clipFunc on Group. */
function AvatarClip({ url, radius }: { url: string; radius: number }) {
  const img = useImage(url);
  if (!img) return null;
  return (
    <Group
      clipFunc={(ctx) => {
        ctx.arc(0, 0, radius, 0, Math.PI * 2, false);
      }}
      listening={false}
    >
      <KonvaImage image={img} x={-radius} y={-radius} width={radius * 2} height={radius * 2} />
    </Group>
  );
}

/**
 * Ball token rendered from the bundled SVG asset. Falls back to a plain
 * white disc with the ⚽ glyph while the image is still loading so the
 * board never shows an empty hit-target.
 */
function BallToken({
  token,
  radius,
  selected,
  onSelect,
  onDragStart,
  onDragMove,
  onDragEnd,
  registerNode,
}: {
  token: Token;
  radius: number;
  selected: boolean;
  onSelect: (id: string | null) => void;
  onDragStart?: (id: string, x: number, y: number) => void;
  onDragMove?: (id: string, x: number, y: number) => void;
  onDragEnd: (id: string, x: number, y: number) => void;
  registerNode: (id: string, node: Konva.Group | null) => void;
}) {
  const img = useImage(soccerBallUrl);
  const size = radius * 2;
  return (
    <Group
      id={`tok-${token.id}`}
      x={token.x}
      y={token.y}
      draggable
      ref={(node) => registerNode(token.id, node)}
      onMouseDown={(e) => {
        e.cancelBubble = true;
        if (!selected) onSelect(token.id);
      }}
      onTouchStart={(e) => {
        e.cancelBubble = true;
        if (!selected) onSelect(token.id);
      }}
      onDragStart={(e) => onDragStart?.(token.id, e.target.x(), e.target.y())}
      onDragMove={(e) => onDragMove?.(token.id, e.target.x(), e.target.y())}
      onDragEnd={(e) => onDragEnd(token.id, e.target.x(), e.target.y())}
    >
      {selected && (
        <Circle
          radius={radius + 6}
          stroke="#97fc00"
          strokeWidth={3}
          dash={[6, 4]}
          listening={false}
        />
      )}
      {/* Hit target — invisible but keeps the ball draggable while the
          SVG is loading and provides a comfortable touch radius. */}
      <Circle radius={radius} fill="rgba(0,0,0,0.001)" />
      {img ? (
        <KonvaImage
          image={img}
          x={-radius}
          y={-radius}
          width={size}
          height={size}
          shadowColor="black"
          shadowBlur={6}
          shadowOpacity={0.35}
          shadowOffsetY={2}
          listening={false}
        />
      ) : (
        <>
          <Circle
            radius={radius}
            fill="#ffffff"
            stroke="#101418"
            strokeWidth={3}
            listening={false}
          />
          <Text
            text="⚽"
            fontSize={radius * 1.4}
            x={-radius}
            y={-radius * 0.85}
            width={size}
            align="center"
            listening={false}
          />
        </>
      )}
    </Group>
  );
}
